import { useEffect, useState } from "react";
import { useTranslate } from "react-admin";

import { FormHelperText } from "@mui/material";
import { SmartAccountType } from "@wallet-manager/node-types/dist/src/postgres/const";
import EnumWalletType from "@wallet-manager/node-types/dist/types/postgres/const/WalletType";

import { getWalletManagement } from "../api/merchant";
import { useAlerting } from "../hooks";
import { useChains } from "./FetchConfig";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "./MuiGenerals";
import { isNil, isNull, toInteger } from "lodash";

interface ChoicesFace {
  wallet_address: string;
  display_name: string;
  is_default: boolean;
  smart_account_type: SmartAccountType | null;
}
export default function AddressSelection(props: {
  wallet_types: Array<EnumWalletType>;
  chain_name: string;
  gather_address: string;
  setGatherAddress: (value: string) => void;
  helperText?: string;
  tryDefaultWallet?: boolean;
  isDisabled?: boolean;
  setSmartAccountType?: (value: SmartAccountType | null) => void;
  walletMajorVersion?: number;
  isShowOwnerWalletDetails?: boolean;
}) {
  const translate = useTranslate();
  const t = (key: string) => translate(`assetManagement.${key}`);
  const {
    wallet_types,
    chain_name = "",
    gather_address = "",
    setGatherAddress,
    helperText,
    tryDefaultWallet,
    isDisabled,
    setSmartAccountType,
    walletMajorVersion,
    isShowOwnerWalletDetails,
  } = props;
  const [initChoices, setInitChoices] = useState<ChoicesFace[]>([]);
  const [choices, setChoices] = useState<ChoicesFace[]>([]);
  const { selectChainByPieces } = useChains();
  const { alerting } = useAlerting();
  const filterWalletStatus = [1];

  const { chain_id, chain_type } = selectChainByPieces({ chain_name });
  const params = {
    chain_type,
    chain_id,
    wallet_types,
    statuses: filterWalletStatus,
    page: 0,
    pageSize: 100,
    getOwnerWalletDetails: isShowOwnerWalletDetails,
  };
  useEffect(() => {
    if (gather_address !== "") {
      return;
    }
    if (choices.length === 0 || !tryDefaultWallet) {
      return setGatherAddress("");
    }
    const defaultAddress =
      choices.find((w) => w.is_default)?.wallet_address || "";
    setGatherAddress(defaultAddress);
  }, [choices, tryDefaultWallet]);

  useEffect(() => {
    //always clear gather address choices and actual value, if chain_name changes
    setGatherAddress("");
    setChoices([]);
    if (!chain_name) return;
    let isMounted = true;
    const controller = new AbortController();
    const signal = controller.signal;
    const fetch = async () => {
      if (!isMounted) return;
      const { rows = [] } = await getWalletManagement(params, { signal });
      setInitChoices(rows);
      const noResult = chain_name.length > 0 && rows.length === 0;
      if (noResult) alerting("warning", t("noWalletAddressError"));
    };
    fetch();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [
    chain_name,
    wallet_types.join(",") /* update address when wallet type change */,
  ]);

  useEffect(() => {
    if (initChoices.length === 0) return;
    setChoices(
      initChoices.filter((item: any) => {
        //skip wallet major verson checking when no wallet major version passed
        if (isNil(walletMajorVersion)) return true;
        if (toInteger(item?.wallet_version / 100) === walletMajorVersion) {
          return true;
        }
        if (!isNil(item?.owner_wallet?.wallet_version)) {
          if (
            toInteger(item?.owner_wallet?.wallet_version / 100) ===
            walletMajorVersion
          ) {
            return true;
          }
        }
        return false;
      }),
    );
  }, [initChoices, walletMajorVersion]);

  const onSelectChange = (e: SelectChangeEvent<string>) => {
    setGatherAddress(e.target.value);
    setSmartAccountType?.(
      choices.find((c) => c.wallet_address === e.target.value)!
        .smart_account_type,
    );
  };
  return (
    <FormControl sx={{ position: "relative", minWidth: "15rem" }}>
      <InputLabel>{!isDisabled && t("phAddress")}</InputLabel>
      <Select
        name="gather_address"
        defaultValue=""
        onChange={onSelectChange}
        value={gather_address}
        disabled={isDisabled}>
        {choices.map((key, i) => (
          <MenuItem key={i} value={key.wallet_address}>
            [{key.display_name}] {key.wallet_address}
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <FormHelperText sx={{ position: "absolute", bottom: -15, left: -14 }}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}
